import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

/**
 * AdaContext — plumbing for the persistent Ada bubble.
 *
 * Pages (DataRoom / DocumentViewer / DocumentsTab) publish where the
 * user's focus currently is; the AdaBubble reads it to:
 *   • Show a "Context: 📄 File.pdf" chip above the composer.
 *   • Send that focus with every /ada/chat POST so Ada can bias
 *     retrieval + interpret pronouns like "this", "here", "it".
 *   • Auto-dock to a smaller size when a document is being viewed
 *     (so it doesn't cover the reader), and restore when the user
 *     returns to a folder / room listing.
 *
 * Ada also remembers its OWN open/closed state here so navigating
 * from a folder into a doc doesn't blink it out.
 */
const AdaCtx = createContext(null);

const OPEN_KEY = "ada.window.open.v1";

export function AdaContextProvider({ children }) {
  const [activeContext, setActiveContext] = useState(null);
  // Persistent open/closed state — survives route transitions.
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(OPEN_KEY);
      return raw === "1";
    } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, isOpen ? "1" : "0"); }
    catch { /* quota — non-fatal */ }
  }, [isOpen]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const clearContext = useCallback(() => setActiveContext(null), []);

  const value = useMemo(() => ({
    activeContext, setActiveContext, clearContext,
    isOpen, open, close, toggle,
  }), [activeContext, isOpen, open, close, toggle, clearContext]);

  return <AdaCtx.Provider value={value}>{children}</AdaCtx.Provider>;
}

export function useAdaContext() {
  const v = useContext(AdaCtx);
  if (!v) {
    // Rendering outside the provider (e.g. login page) — return
    // a no-op object so consumers don't crash.
    return {
      activeContext: null, setActiveContext: () => {}, clearContext: () => {},
      isOpen: false, open: () => {}, close: () => {}, toggle: () => {},
    };
  }
  return v;
}

/**
 * Publish an active focus (folder / document) from a page. Cleans up
 * automatically on unmount so leaving the page drops the context.
 *
 * @param {{type: "folder"|"document"|null, folder_id?, doc_id?, name?, path?, page?} | null} ctx
 */
export function useActiveContext(ctx) {
  const { setActiveContext, clearContext } = useAdaContext();
  useEffect(() => {
    if (ctx && ctx.type) setActiveContext(ctx);
    else clearContext();
    return () => clearContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ctx?.type, ctx?.folder_id, ctx?.doc_id, ctx?.name, ctx?.page,
  ]);
}
